import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Link, MemoryRouter, NavLink, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router';
import { AppContext, useApp, useAppPath } from './lib/AppContext';
import { configureApi, createApi } from './lib/api';
import HomePage from './pages/HomePage';
import DayPage from './pages/DayPage';
import TrendsPage from './pages/TrendsPage';
import PlanPage from './pages/PlanPage';
import PlanEditPage from './pages/PlanEditPage';
import SettingsPage from './pages/SettingsPage';
import TargetsPage from './pages/TargetsPage';
import ProfilePage from './pages/ProfilePage';
import CreditsPage from './pages/CreditsPage';
import SecurityPage from './pages/SecurityPage';
import CoachShell from './coach/CoachShell';
import StudentsTab from './coach/StudentsTab';
import PlanTab from './coach/PlanTab';
import RequestsTab from './coach/RequestsTab';
import AccountTab from './coach/AccountTab';
import ModeSheet from './coach/ModeSheet';
import { CoachInviteStep, CoachProfileStep } from './coach/CoachOnboarding';
import SchedulePage from './pages/SchedulePage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import ReminderSync from './components/ReminderSync';
import ActivityPing from './components/ActivityPing';
import { LaunchRedirect, useRememberMode } from './lib/mode';
import CoachIntroPage from './pages/CoachIntroPage';
import CoachCodePage from './pages/CoachCodePage';
import CoachPlanPage from './pages/CoachPlanPage';
import MyCoachPage from './pages/MyCoachPage';
import InvitePage from './pages/InvitePage';
import AcceptPage from './cook/AcceptPage';
import NotificationsPage from './pages/NotificationsPage';
import GiftEndedSheet from './components/GiftEndedSheet';
import ConnectPrompt from './components/ConnectPrompt';
import NameSheet from './components/NameSheet';
import RolePicker from './components/RolePicker';
import Onboarding from './components/Onboarding';
import WelcomePage from './pages/WelcomePage';
import Icon from './components/Icon';
import Avatar from './components/Avatar';
import { CaptureProvider, useCapture } from './components/Capture';
import LoadingBar from './components/LoadingBar';
import ProSheet, { ProBadge } from './components/ProSheet';
import { TrialChip } from './components/Trial';
import OutOfCreditsSheet from './components/OutOfCreditsSheet';
import { AnnouncementsProvider, NewBadge, useAnnouncementAnchor } from './components/Announcements';
import { FeedbackProvider, useFeedback } from './components/FeedbackChat';
import FeedbackButton from './components/FeedbackButton';

// Five slots with the raised capture button in the middle; settings live behind the account button.
const TABS = [
  { to: '/', label: 'Trang chủ', icon: 'home' },
  { to: '/day', label: 'Hôm nay', icon: 'calendar' },
  { capture: true },
  { to: '/plan', label: 'Thực đơn', icon: 'menu', pro: true },
  { to: '/trends', label: 'Thống kê', icon: 'stats' },
];
const OWNER_BASE = '/me';
// Same shape as User::SHARE_CODE_FORMAT, so /day or /settings are never read as a share code.
const SHARE_CODE = /^[1-9A-HJ-NP-Za-km-z]{12}$/;
const ownApi = createApi('/api');

// On a read-only log: a way back to editing your own, or a "view only" note.
function ViewerLink() {
  const { editable, signedIn, base, shareCode, myShareCode, coachClient } = useApp();
  const { pathname, search } = useLocation();
  const rest = `${pathname.slice(base.length)}${search}`;
  const className = 'btn btn-ghost btn-sm h-11 font-medium';

  if (editable) return null;
  // A coach looking at a student's log: whose it is, and the way back to coach mode.
  if (coachClient) {
    return (
      <Link to={`${OWNER_BASE}/coach`} className="flex h-11 items-center gap-1.5 rounded-full bg-base-100 pl-2 pr-3 text-sm font-semibold">
        <Icon name="chevronLeft" className="size-4" />
        <span className="max-w-[9rem] truncate">{coachClient.name || 'Học viên'}</span>
        <span className="rounded-full bg-base-300 px-2 py-0.5 text-[11px]">Chỉ xem</span>
      </Link>
    );
  }
  if (signedIn && shareCode === myShareCode) {
    return <Link to={`${OWNER_BASE}${rest}`} className={className}><Icon name="pencil" className="size-4" /> Chỉnh sửa</Link>;
  }
  // Someone else's log: a full page load, so goals and the signed-in state come from the server again.
  if (signedIn) return <a href={OWNER_BASE} className={className}>Nhật ký của tôi</a>;
  return <span className="rounded-full bg-base-300 px-3 py-1 text-xs font-semibold">Chỉ xem</span>;
}

// The bell, with a dot while something is unread (your own log only). Coach mode has its own bell.
function Bell() {
  const path = useAppPath();
  const { unreadBySide } = useApp();
  const unread = unreadBySide?.personal || 0;
  return (
    <Link to={path('/notifications')} aria-label={unread > 0 ? `Thông báo, ${unread} chưa đọc` : 'Thông báo'}
      className="relative grid size-11 place-items-center rounded-full border border-line bg-base-100">
      <Icon name="bell" className="size-5" />
      {unread > 0 && <span className="absolute right-2 top-2 size-2.5 rounded-full bg-error ring-2 ring-base-100" />}
    </Link>
  );
}

function Header({ large }) {
  const path = useAppPath();
  const { profile, editable, coach, unreadBySide } = useApp();
  const accountAnchor = useAnnouncementAnchor('account_button');
  const modeAnchor = useAnnouncementAnchor('mode_switch');
  const [switching, setSwitching] = useState(false);
  const avatar = (
    <span className="grid size-11 place-items-center overflow-hidden rounded-full border border-line bg-base-100">
      {profile ? <Avatar name={profile.name} url={profile.avatar_url} size={44} /> : <Icon name="user" className="size-5" />}
    </span>
  );
  return (
    <header className="pt-safe flex items-center justify-between gap-2 px-4 pb-2 pt-4">
      <Link to={path('/')} className={`font-display ${large ? 'text-[26px]' : 'text-[22px]'}`}>Macro Mate</Link>
      <div className="flex items-center gap-2">
        <TrialChip />
        <ViewerLink />
        {/* Coaches and their students get notifications; others see the bell once something arrives. */}
        {editable && (coach || unreadBySide?.personal > 0) && <Bell />}
        {/* Guests on a share link have no account here. A coach's avatar switches to coach mode (and still
            leads to Settings from there). */}
        {editable && !coach && (
          <Link ref={accountAnchor} to={path('/settings')} aria-label="Tài khoản và cài đặt" className="relative">
            {avatar}<NewBadge anchor="account_button" dot />
          </Link>
        )}
        {editable && coach && (
          <button ref={modeAnchor} type="button" onClick={() => setSwitching(true)} aria-label="Chuyển chế độ hoặc mở cài đặt" className="relative">
            {avatar}<NewBadge anchor="mode_switch" dot />
          </button>
        )}
        {editable && coach && <ModeSheet open={switching} current="personal" onClose={() => setSwitching(false)} />}
      </div>
    </header>
  );
}

function BottomBar() {
  const path = useAppPath();
  const capture = useCapture();
  const { membership } = useApp();
  const tabs = TABS.filter((tab) => !tab.capture || capture.enabled);
  const captureAnchor = useAnnouncementAnchor('capture_button');

  return (
    <nav aria-label="Điều hướng chính" className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-line bg-base-100">
      <div className={`mx-auto grid max-w-xl ${tabs.length === 5 ? 'grid-cols-5' : 'grid-cols-4'}`}>
        {tabs.map((tab) => (tab.capture ? (
          <div key="capture" className="flex justify-center">
            <button ref={captureAnchor} type="button" aria-label="Ghi bữa ăn hoặc tập luyện" onClick={() => capture.open()}
              className="-mt-[26px] grid size-[60px] place-items-center rounded-full bg-primary text-primary-content shadow-lg ring-4 ring-base-100 transition active:scale-95">
              <Icon name="camera" className="size-7" />
            </button>
          </div>
        ) : (
          <NavLink key={tab.to} to={path(tab.to)} end={tab.to === '/'}
            className={({ isActive }) => `flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs ${isActive ? 'font-bold text-brand-text' : 'text-muted'}`}>
            <span className="relative">
              <Icon name={tab.icon} className="size-6" />
              {tab.pro && membership && !membership.pro && <ProBadge className="absolute -right-5 -top-1" />}
            </span>
            {tab.label}
          </NavLink>
        )))}
      </div>
    </nav>
  );
}

// A link into the "Góp ý" chat (a reply's notification, or the old /support page): opens it on that thread,
// over the home screen of the side it belongs to.
function OpenFeedback({ mode }) {
  const { id } = useParams();
  const { openFeedback } = useFeedback();
  const path = useAppPath();
  useEffect(() => { openFeedback({ mode, threadId: id }); }, [openFeedback, mode, id]);
  return <Navigate to={path(mode === 'coach' ? '/coach' : '/')} replace />;
}

// Account pages exist only on your own log; a guest on a share link is sent to the day instead.
function OwnerOnly({ children }) {
  const { editable } = useApp();
  const path = useAppPath();
  return editable ? children : <Navigate to={path('/day')} replace />;
}

// Once each (the server remembers): the 7-day trial is offered to an account that hasn't tried Pro (a new account
// is offered it at the end of onboarding; this catches one that closed the app there), and again once after its
// first AI meal is saved ("Bữa đầu tiên đã ghi", even after "Để sau" at onboarding). When a trial has ended, the
// 7 days' summary and the plans are shown. Never in coach mode, which has its own shell.
function TrialPrompt() {
  const { editable, membership, openPaywall, profile } = useApp();
  // Not over the role question, onboarding and the name question, which come first.
  const ready = editable && membership && !profile?.needsRole && !profile?.needsOnboarding && !profile?.needsName;
  const reason = !ready ? null : membership.trial_ended ? 'trial_ended'
    : !membership.trial_available ? null
      : !membership.prompted ? 'trial_offer' : membership.first_ai_offer ? 'first_ai' : null;
  useEffect(() => {
    if (!reason) return undefined;
    // After a save the capture sheet closes first: the offer never cuts into the save.
    const timer = setTimeout(() => openPaywall(reason, { placement: reason === 'trial_offer' ? 'onboarding' : reason }), 1200);
    return () => clearTimeout(timer);
  }, [reason, openPaywall]);
  return null;
}

// Home is dark; Today, the other screens and the sheets are light.
function AppShell({ dark = false, header = true, children }) {
  useRememberMode();
  return (
    <div data-theme={dark ? 'macro-dark' : 'macro'} className="min-h-dvh bg-base-200 text-base-content">
      <CaptureProvider>
        <LoadingBar />
        <TrialPrompt />
        <div className="mx-auto flex min-h-dvh max-w-xl flex-col">
          {header && <Header large={dark} />}
          <main className="flex-1 px-4 pb-32">{children}</main>
          <BottomBar />
          <FeedbackButton mode="personal" />
        </div>
      </CaptureProvider>
    </div>
  );
}

// The same pages are mounted twice: your own log, editable, under /me (the server makes you sign in
// first), and someone's log, read-only, under the share code from their link.
// A coach also opens a connected student's log read-only, under /me/clients/<id>.
function Scope({ base, shareCode = null, coachClient = null }) {
  const parent = useApp();
  const value = useMemo(() => ({
    ...parent,
    base,
    shareCode,
    coachClient,
    editable: base === OWNER_BASE,
    api: shareCode ? createApi('/api', shareCode) : coachClient ? createApi('/api', null, { coachClient: coachClient.id }) : ownApi,
  }), [parent, base, shareCode, coachClient]);
  // Announcements of new features, reminders and activity recording are for your own account only.
  return (
    <AppContext.Provider value={value}>
      {value.editable ? <AnnouncementsProvider><ReminderSync /><ActivityPing /><Outlet /></AnnouncementsProvider> : <Outlet />}
    </AppContext.Provider>
  );
}

function ClientScope() {
  const { clientId } = useParams();
  const { state } = useLocation();
  // The name comes from the Học viên tab; the header falls back to "Học viên" on a reload.
  const [name] = useState(state?.clientName || null);
  const client = useMemo(() => ({ id: clientId, name }), [clientId, name]);
  return <Scope base={`${OWNER_BASE}/clients/${clientId}`} coachClient={client} />;
}

function SharedScope() {
  const { code } = useParams();
  return SHARE_CODE.test(code) ? <Scope base={`/${code}`} shareCode={code} /> : <WelcomePage />;
}

// `native` is set inside the iOS/Android app: { signOut, forget, siteUrl }. The app has no address bar, so it
// routes in memory and opens on your own log.
export default function App({
  user, signed_in: signedIn, my_share_code: initialShareCode, membership: initialMembership = null, admin = false, native = null,
  legacy_phone: legacyPhone = false, coach: initialCoach = null, unread: initialUnread = null, reminders: initialReminders = null,
  feedback: initialFeedback = null,
}) {
  const [goals, setGoals] = useState({
    calories: user?.calorie_goal, protein_g: user?.protein_goal, workout_protein_g: user?.workout_protein_goal,
  });
  // Settings can create, change or remove your share code without a reload.
  const [myShareCode, setMyShareCode] = useState(initialShareCode);
  // Free or Pro and today's AI credits (null on someone else's shared log). The Pro sheet opens with a
  // reason: trial_offer, trial_ended, pro_required, credits or upgrade.
  const [membership, setMembership] = useState(initialMembership);
  // Your name and picture for the header's account button (only on your own log, where membership is set), and
  // whether the app still has to ask a new account self or coach (RolePicker), run its onboarding (Onboarding)
  // and ask its name (NameSheet: accounts from before onboarding).
  const [profile, setProfile] = useState(initialMembership
    ? { name: user?.name, avatar_url: user?.avatar_url, needsName: !!user?.needs_name, needsRole: !!user?.needs_role, needsOnboarding: !!user?.needs_onboarding }
    : null);
  // { reason, options }: reason is trial_offer, trial_ended, pro_required, credits or upgrade; credits can carry
  // options.manual = { label, onClick }, the way to carry on by hand from the form that was refused, and
  // options.retry, which sends that request again once an ad gave a credit.
  const [paywall, setPaywall] = useState(null);
  // Coach mode ({ code, approval_required, gym, launch_mode, requests_count, students_count } or null), the
  // unread counts of each side's bell ({ coach, personal }) and the phone's reminder settings.
  const [coach, setCoach] = useState(initialCoach);
  const [unreadBySide, setUnreadBySide] = useState(initialUnread || { coach: 0, personal: 0 });
  const [reminders, setReminders] = useState(initialReminders);
  // On a coach-mode screen (or a student's log) right now: the personal app's prompts wait for the personal app.
  const [coachView, setCoachView] = useState(false);
  const openPaywall = useMemo(() => (reason, options = {}) => setPaywall({ reason: reason || 'upgrade', options }), []);
  useEffect(() => {
    configureApi({
      onMembership: setMembership,
      onDenied: (data) => {
        if (data.membership) setMembership(data.membership);
        // Out of credits, end of the month's allowance or the week's menus: the sheet shows the server's reason.
        const limited = ['credits', 'menu_limit'].includes(data.code);
        setPaywall({ reason: limited ? 'credits' : 'pro_required', options: limited ? { message: data.error } : {} });
      },
    });
  }, []);
  // `buying`: closed by "Đăng ký Pro" (the chat opens to buy), so not "Để sau".
  const closePaywall = ({ buying = false } = {}) => {
    // Closing a trial offer without starting it is "Để sau" (recorded with where it was); closing "Hết 7 ngày dùng
    // thử Pro" means it was seen.
    if (!buying && ['trial_offer', 'first_ai'].includes(paywall?.reason) && membership?.trial_available) {
      ownApi.post('/membership/prompted', { placement: paywall.options.placement || 'paywall' }).then(setMembership).catch(() => {});
    }
    if (paywall?.reason === 'trial_ended') ownApi.post('/membership/trial_end_seen').then(setMembership).catch(() => {});
    setPaywall(null);
  };
  const context = useMemo(
    () => ({ api: ownApi, goals, setGoals, signedIn, myShareCode, setMyShareCode, native, membership, setMembership, openPaywall, admin, profile, setProfile,
      coach, setCoach, unreadBySide, setUnreadBySide, reminders, setReminders, setCoachView }),
    [goals, signedIn, myShareCode, native, membership, openPaywall, admin, profile, coach, unreadBySide, reminders],
  );
  const shell = (page) => <AppShell>{page}</AppShell>;
  const pages = (
    <>
      <Route index element={<LaunchRedirect><AppShell dark><HomePage /></AppShell></LaunchRedirect>} />
      <Route path="day" element={shell(<DayPage />)} />
      <Route path="trends" element={shell(<TrendsPage />)} />
      <Route path="plan" element={shell(<PlanPage />)} />
      <Route path="plan/:id" element={shell(<PlanEditPage />)} />
      <Route path="settings" element={<OwnerOnly><AppShell header={false}><SettingsPage /></AppShell></OwnerOnly>} />
      <Route path="settings/security" element={<OwnerOnly><AppShell header={false}><SecurityPage /></AppShell></OwnerOnly>} />
      {/* Also for share links: a PT can see what each day was measured against. */}
      <Route path="targets" element={<AppShell header={false}><TargetsPage /></AppShell>} />
      <Route path="profile" element={<OwnerOnly><AppShell header={false}><ProfilePage /></AppShell></OwnerOnly>} />
      <Route path="credits" element={<OwnerOnly><AppShell header={false}><CreditsPage /></AppShell></OwnerOnly>} />
      <Route path="*" element={<AppShell dark><HomePage /></AppShell>} />
    </>
  );
  // Coach mode and the student's side of it, on your own account only.
  const own = (page, header = false) => <OwnerOnly><AppShell header={header}>{page}</AppShell></OwnerOnly>;
  const ownerPages = (
    <>
      {/* Coach mode: its own shell and tabs (app/javascript/app/coach). */}
      <Route path="coach" element={<OwnerOnly><CoachShell /></OwnerOnly>}>
        <Route index element={<StudentsTab />} />
        <Route path="plan" element={<PlanTab />} />
        <Route path="requests" element={<RequestsTab />} />
        <Route path="account" element={<AccountTab />} />
        <Route path="code" element={<CoachCodePage />} />
        <Route path="seats" element={<CoachPlanPage />} />
        <Route path="notifications" element={<NotificationsPage audience="coach" />} />
      </Route>
      <Route path="coach/intro" element={own(<CoachIntroPage />)} />
      <Route path="coach/feedback/:id" element={<OwnerOnly><OpenFeedback mode="coach" /></OwnerOnly>} />
      <Route path="feedback/:id" element={<OwnerOnly><OpenFeedback mode="personal" /></OwnerOnly>} />
      <Route path="support" element={<OwnerOnly><OpenFeedback mode="personal" /></OwnerOnly>} />
      {/* The coach's onboarding (O3, O4), after choosing "Tôi là huấn luyện viên" or turning coach mode on. */}
      <Route path="coach/start" element={<OwnerOnly><CoachProfileStep /></OwnerOnly>} />
      <Route path="coach/start/invite" element={<OwnerOnly><CoachInviteStep /></OwnerOnly>} />
      {/* Before coach mode had its own tabs, the students were here (old notifications link to it). */}
      <Route path="students" element={<Navigate to={`${OWNER_BASE}/coach`} replace />} />
      <Route path="my-coach" element={own(<MyCoachPage />)} />
      <Route path="c/:code" element={own(<MyCoachPage />)} />
      <Route path="schedule" element={own(<SchedulePage />)} />
      <Route path="settings/notifications" element={own(<NotificationSettingsPage />)} />
      <Route path="notifications" element={own(<NotificationsPage audience="personal" />)} />
      <Route path="i/:token" element={<OwnerOnly><InvitePage /></OwnerOnly>} />
      <Route path="n/:token" element={<OwnerOnly><AcceptPage /></OwnerOnly>} />
    </>
  );

  const routes = (
    <Routes>
      <Route path={`${OWNER_BASE}/clients/:clientId`} element={<ClientScope />}>{pages}</Route>
      <Route path={OWNER_BASE} element={<Scope base={OWNER_BASE} />}>{ownerPages}{pages}</Route>
      {/* An invite opened signed out (the website); signing in comes back to /me/i/<token>. */}
      <Route path="i/:token" element={<InvitePage />} />
      <Route path="n/:token" element={<AcceptPage />} />
      <Route path=":code" element={<SharedScope />}>{pages}</Route>
      <Route path="*" element={<WelcomePage />} />
    </Routes>
  );

  const personalPrompts = initialMembership && !profile?.needsRole && !profile?.needsOnboarding && !coachView;
  const firstRun = initialMembership && <><RolePicker base={OWNER_BASE} /><Onboarding base={OWNER_BASE} /></>;
  // Inside the chat's provider: "Đăng ký Pro" opens the chat with the team until payments go through the App Store.
  const paywallSheets = (
    <>
      <ProSheet reason={paywall?.reason === 'credits' ? null : paywall?.reason} placement={paywall?.options?.placement} onClose={closePaywall} />
      <OutOfCreditsSheet open={paywall?.reason === 'credits'} manual={paywall?.options?.manual} message={paywall?.options?.message}
        retry={paywall?.options?.retry} onClose={closePaywall}
        onUpgrade={() => setPaywall({ reason: 'upgrade', options: { placement: 'quota' } })} />
    </>
  );

  return (
    <AppContext.Provider value={context}>
      {/* The personal app's questions and prompts: after the role question and onboarding, never over coach mode. */}
      {personalPrompts && <NameSheet />}
      {personalPrompts && !profile?.needsName && <ConnectPrompt legacyPhone={legacyPhone} />}
      {personalPrompts && !profile?.needsName && <GiftEndedSheet />}
      {native
        ? <MemoryRouter initialEntries={[OWNER_BASE]}><FeedbackProvider initial={initialFeedback}>{firstRun}{routes}{paywallSheets}</FeedbackProvider></MemoryRouter>
        : <BrowserRouter><FeedbackProvider initial={initialFeedback}>{firstRun}{routes}{paywallSheets}</FeedbackProvider></BrowserRouter>}
    </AppContext.Provider>
  );
}
